// Request plugin.
import * as actionTypes from './interface/actionTypes'
import * as actions from './interface/actions'
import makeRequest from './makeRequest'
import * as authorizations from './authorization'
import { sanitizeRequest } from './utils'

// Other plugins.
import { logManager } from '../logs'

// Helpers.
import { getVersion } from '../common/version'

// Libraries.
import { call, put, takeEvery } from 'redux-saga/effects'

/*
 * Saga-factory for creating the `watchRequests` saga.
 *    If `platform` is undefined, then no extra, platform-specific handling is done.
 */
export default function watchRequests (platform) {
  // Only allow our known platforms or undefined (for base).
  if (!['link', 'uc', 'cpaas', undefined].includes(platform)) {
    throw Error('Invalid platform specific for requests.')
  }
  return function * watchRequests () {
    yield takeEvery(actionTypes.REQUEST, handleRequestSaga, platform)
  }
}

/*
 * Saga that handles the request when it is triggered by an action.
 */
function * handleRequestSaga (platform, action) {
  const result = yield call(handleRequest, platform, action)

  // If the platform was specified and this is not a 3.X build, perform the
  //    authorization check side-effect.
  if (platform && !getVersion().startsWith('3')) {
    // Call the 'authorization' function specific for this platform.
    //    They're all named ${platform}Authorization so it's easier to call them.
    const error = authorizations[`${platform}Authorization`](result)

    if (error) {
      yield put(actions.authorizationError(error))
    }
  }

  yield put(actions.response(action.meta.requestId, result, !!result.error))
}

/*
 * Generator that handles a request action with standard HTTP handling features.
 *
 * @param {string} [platform] The platform being used.
 * @param {FluxStandardAction} action The action to handle.
 * @return {Object} The results object.
 */
export function * handleRequest (platform, action) {
  const log = logManager.getLogger('REQUEST', action.meta.requestId)

  const logOptions = sanitizeRequest(action.payload)
  log.debug(`Making REST request ${action.meta.requestId}.`, logOptions)

  // Make the request based on the action
  const result = yield makeRequest(action.payload, action.meta.requestId)

  log.debug(`Received REST response ${action.meta.requestId}.`, result)

  return result
}

// begin-test-code
export const __testonly__ = { watchRequests, handleRequestSaga }
// end-test-code
